import { useCallback, useEffect, useState } from "react";
import { Alert, Box, LinearProgress, MenuItem, TextField, Typography } from "@mui/material";
import { Download, Refresh } from "@mui/icons-material";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import useClientPagination from "../../common/use-client-pagination";
import AppChip from "../../common/app-chip";
import CustomTabs from "../../common/custom-tabs";
import StatTile from "../../common/stat-tile";
import { INSTAGRAM_ACCOUNTS, INSTAGRAM_ANALYTICS } from "../../config/urls";
import { TRIGGER_LABEL } from "./InstagramFlow";
import InstagramBanners from "./InstagramBanners";
import { RolesTab } from "./InstagramAdminTabs";
import { Section, errorMessage, useInstagramApi } from "./shared";

// "Instagram Analytics" (spec 10.7): per account and per rule, for a date range, with CSV export.

const isoDay = (d) => d.toLocaleDateString("en-CA"); // YYYY-MM-DD in the browser's timezone
const daysAgo = (n) => isoDay(new Date(Date.now() - n * 86400000));
const pct = (value) => (value == null ? "—" : `${value}%`);

const duration = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  return `${hours}h ${Math.round((seconds % 3600) / 60)}m`;
};

const RULE_COLUMNS = [
  { id: "name", label: "Rule" },
  { id: "trigger", label: "Trigger", csv: (r) => TRIGGER_LABEL[r.trigger_type] || r.trigger_type },
  { id: "triggered", label: "Triggered" },
  { id: "comments", label: "Comments" },
  { id: "dms_sent", label: "DMs sent" },
  { id: "taps", label: "Quick-reply taps" },
  { id: "tap_rate", label: "Tap rate", csv: (r) => pct(r.tap_rate) },
  { id: "handovers", label: "Handed to agents" },
  { id: "failed", label: "Failed" },
];

// Same palette as the WhatsApp template analytics chart.
const DAILY_LINES = [
  { key: "comments", name: "Comments", color: "#651FFF" },
  { key: "dms_sent", name: "Automated DMs", color: "#00BFA5" },
  { key: "agent_replies", name: "Agent replies", color: "#00796B" },
  { key: "handovers", name: "Handed to agents", color: "#FFA000" },
  { key: "failures", name: "Failures", color: "#FF5252" },
];
const shortDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

// Spec 11 plan limits. A limit of 0 (or none) means unlimited, see service.plan_limit.
const PLAN_ITEMS = [
  { key: "automated_dms", title: "Automated DMs this month", help: "DMs sent by your automations. Resets on the 1st." },
  { key: "rules", title: "Active automations", help: "Rules that are switched on. Ice breakers don't count." },
  { key: "accounts", title: "Instagram accounts", help: "Connected accounts. Disconnected ones don't count." },
  { key: "retention_days", title: "Analytics history", help: "How far back Analytics can show data.", unit: "days" },
];

const UsageCard = ({ title, help, unit, usage }) => {
  const { used, limit } = usage || {};
  const percent = limit && used != null ? Math.min(100, Math.round((used / limit) * 100)) : null;
  const color = percent >= 100 ? "error" : percent >= 80 ? "warning" : "primary";
  let value = limit ? `${limit} ${unit}` : "Unlimited";
  if (!unit) value = limit ? `${used ?? "—"} / ${limit}` : `${used ?? "—"} used`;
  return (
    <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "10px", p: 2.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="body1" fontWeight={600}>{title}</Typography>
        <Typography variant="h6" fontWeight={700} color={percent >= 100 ? "error.main" : "text.primary"}>
          {value}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">{help}</Typography>
      {!unit && (
        <Box mt={1.5}>
          <LinearProgress variant="determinate" color={color} value={percent ?? 0}
            sx={{ height: 8, borderRadius: 4, bgcolor: "#F3F5F7" }} />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {percent == null ? "No limit on your plan" : `${percent}% used`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const downloadCsv =(filename, rows) => {
  const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const InstagramAnalytics = () => {
  const { request, toast } = useInstagramApi();
  const [accounts, setAccounts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [role, setRole] = useState("");
  const [tab, setTab] = useState("analytics");
  const [filters, setFilters] = useState({ account: "", from: daysAgo(6), to: daysAgo(0) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    request("get", INSTAGRAM_ACCOUNTS).then((res) => {
      setAccounts(res.accounts || []);
      setUsage({ ...res.usage, accounts: { used: res.used, limit: res.limit } });
      setRole(res.role || "");
    }).catch(() => {});
  }, [request]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await request("get", INSTAGRAM_ANALYTICS(filters)));
    } catch (err) {
      setError(errorMessage(err, "Could not load analytics."));
    } finally {
      setLoading(false);
    }
  }, [request, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const totals = data?.totals || {};
  // Only rules that did something in the range; a long list of zeros hides the useful rows.
  const rules = (data?.rules || []).filter((r) => r.triggered || r.dms_sent || r.comments || r.failed);
  const accountName = (id) => (id ? `@${accounts.find((a) => a.id === id)?.username || id}` : "All accounts");
  // Paged on screen only; Export CSV still writes every row.
  const rulesPaging = useClientPagination(rules, filters);
  const failuresPaging = useClientPagination(data?.failures, filters);

  // The "By rule" table as a plain CSV: one header row, one row per rule.
  const exportCsv = () =>
    downloadCsv(`instagram_analytics_${data.from}_${data.to}.csv`, [
      RULE_COLUMNS.map((c) => c.label),
      ...rules.map((r) => RULE_COLUMNS.map((c) => (c.csv ? c.csv(r) : r[c.id]))),
    ]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100%" }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Instagram Analytics</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        How your Instagram automations and agents are doing.
      </Typography>
      <InstagramBanners />

      <CustomTabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, maxWidth: 800 }}
        tabs={[
          { value: "analytics", label: "Analytics" },
          { value: "plan", label: "Plan & billing" },
          { value: "roles", label: "Roles" },
        ]} />

      {tab === "roles" && <RolesTab role={role} />}

      {tab === "plan" && (
        <Section title="Plan usage" hint="What your plan includes for Instagram and how much of it you've used.">
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            {PLAN_ITEMS.map((item) => (
              <UsageCard key={item.key} {...item} usage={item.key === "retention_days"
                ? { limit: data?.retention_days } : usage?.[item.key]} />
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <b>How Instagram is billed:</b> Meta doesn't charge per Instagram message, so nothing is taken from your
            wallet for Instagram. Instagram is part of your plan, with the limits above.
            <br />
            At 80% of the monthly DM limit, workspace admins get an email. At 100%, automations pause until the
            1st of next month, and agent replies keep working. To raise a limit, contact support or upgrade your plan.
          </Alert>
        </Section>
      )}

      {tab === "analytics" && (<>
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={3}>
        <TextField select size="small" label="Account" value={filters.account} onChange={setFilter("account")}
          sx={{ minWidth: 200 }}>
          <MenuItem value="">All accounts</MenuItem>
          {accounts.map((a) => <MenuItem key={a.id} value={String(a.id)}>@{a.username}</MenuItem>)}
        </TextField>
        <TextField type="date" size="small" label="From" value={filters.from} onChange={setFilter("from")}
          inputProps={{ max: filters.to }} InputLabelProps={{ shrink: true }} />
        <TextField type="date" size="small" label="To" value={filters.to} onChange={setFilter("to")}
          inputProps={{ min: filters.from, max: daysAgo(0) }} InputLabelProps={{ shrink: true }} />
        <Box flex={1} />
        <ButtonOne variant="outlined" startIcon={<Refresh />} title="Refresh" onClick={load} disabled={loading} />
        <ButtonOne variant="outlined" startIcon={<Download />} title="Export CSV" onClick={exportCsv} disabled={!data} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {(data?.own_chats_only || data?.retention_days) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.own_chats_only && "Showing chats assigned to you. "}
          {data.retention_days && `Your plan keeps ${data.retention_days} days of analytics.`}
        </Alert>
      )}

      <Box display="flex" flexWrap="wrap" gap={1.5} mb={3}>
        <StatTile value={totals.comments_received ?? "—"} label="Comments received" />
        <StatTile value={totals.dms_sent ?? "—"} label="Automated DMs sent" />
        <StatTile value={pct(totals.tap_rate)} label="Quick-reply tap rate" />
        <StatTile value={totals.handovers ?? "—"} label="Handed to agents" />
        <StatTile value={duration(totals.median_first_response_seconds)} label="Median first response" />
        <StatTile value={totals.failures ?? "—"} label="Failures"
          valueColor={totals.failures ? "#d32f2f" : undefined} />
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "3fr 2fr" }} gap={3} mb={3}>
        <Section title="Activity over time" hint="Per day, in the selected range.">
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.daily || []} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={shortDay} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={shortDay} />
                <Legend />
                {DAILY_LINES.map((l) => (
                  <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Section>
        <Section title="Top rules" hint="Automated DMs sent per rule.">
          <Box sx={{ height: 300 }}>
            {rules.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  data={[...rules].sort((a, b) => b.dms_sent - a.dms_sent).slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="dms_sent" name="DMs sent" fill="#00BFA5" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="failed" name="Failed" fill="#FF5252" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" pt={8}>
                No automation ran in this period
              </Typography>
            )}
          </Box>
        </Section>
      </Box>

      <Section title="By rule" hint="Tap rate counts quick-reply taps; Instagram doesn't report taps on link buttons."
        sx={{ mb: 3 }}>
        <AppTable
          loading={loading}
          maxHeight={480}
          rows={rulesPaging.pageRows}
          {...rulesPaging.pagination}
          emptyMessage="No automation ran in this period"
          columns={RULE_COLUMNS.map((c) => {
            if (c.id === "name") {
              return { ...c, render: (r) => (
                <Box>
                  <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {accountName(r.account_id)}{!r.is_active && " · paused"}
                  </Typography>
                </Box>
              ) };
            }
            return c.csv ? { ...c, render: c.csv } : c;
          })}
        />
      </Section>

      <Section title="Failures by error type" hint="Sends that failed, and rules blocked before sending.">
        <AppTable
          loading={loading}
          maxHeight={360}
          rows={failuresPaging.pageRows}
          {...failuresPaging.pagination}
          rowKey={(f) => `${f.type}:${f.example}`}
          emptyMessage="No failures in this period"
          columns={[
            { id: "type", label: "Type", render: (f) => <AppChip label={f.type} color="error" variant="outlined" /> },
            { id: "example", label: "Error" },
            { id: "count", label: "Count", align: "right" },
          ]}
        />
      </Section>
      </>)}
      {toast}
    </Box>
  );
};

export default InstagramAnalytics;
